import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from '@/components/shared/Placeholders';
import { ROUTES } from '@/constants/routes';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { notify, userAuthService } from '@/services';

type Step = 1 | 2 | 3;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { loading, run } = useAsyncAction();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleRequestCode(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      try {
        const res = await userAuthService.forgotPassword(email.trim());
        notify.success(res.message || 'Verification code sent');
        setStep(2);
      } catch (err) {
        notify.apiError(err, 'Unable to send recovery code');
      }
    });
  }

  async function handleVerifyCode(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      try {
        const res = await userAuthService.verifyResetCode(email.trim(), code.trim());
        notify.success(res.message || 'Code verified');
        setStep(3);
      } catch (err) {
        notify.apiError(err, 'Invalid verification code');
      }
    });
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      notify.error('Passwords do not match');
      return;
    }

    await run(async () => {
      try {
        const res = await userAuthService.resetPassword({
          email: email.trim(),
          code: code.trim(),
          password,
        });
        notify.success(res.message || 'Password updated successfully');
        navigate(ROUTES.login);
      } catch (err) {
        notify.apiError(err, 'Unable to reset password');
      }
    });
  }

  const titles = {
    1: 'Forgot password',
    2: 'Enter verification code',
    3: 'Set new password',
  } as const;

  const subtitles = {
    1: 'Enter your account email and we will send a recovery code',
    2: 'Check your inbox for the 4-digit code we sent you',
    3: 'Choose a new password for your account',
  } as const;

  return (
    <AuthShell
      title={titles[step]}
      subtitle={subtitles[step]}
      loading={loading}
      loadingLabel="Please wait..."
      footer={
        <>
          Remember your password?{' '}
          <Link to={ROUTES.login} className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {step === 1 && (
        <form onSubmit={(e) => void handleRequestCode(e)} className="space-y-4">
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <Button type="submit" fullWidth loading={loading} loadingText="Sending code...">
            Send recovery code
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={(e) => void handleVerifyCode(e)} className="space-y-4">
          <Input label="Email" type="email" value={email} disabled />
          <Input
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            placeholder="4-digit code"
            required
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button type="submit" className="flex-1" loading={loading} loadingText="Verifying...">
              Continue
            </Button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={(e) => void handleResetPassword(e)} className="space-y-4">
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="6–12 characters"
            required
          />
          <Input
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            required
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button type="submit" className="flex-1" loading={loading} loadingText="Updating...">
              Reset password
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
